import React from "react";
import { Taxonomy, TransactionDirectionType } from "@common/categories";
import { getMainCategory } from "@common/helpers/taxonomy";
import { useCategoriesById } from "@hooks/useCategoriesById";
import AdvertisingMarketingIcon from "@icons/transactionCategories/AdvertisingMarketing.svg?react";
import AssetsIcon from "@icons/transactionCategories/Assets.svg?react";
import AutoVehicleIcon from "@icons/transactionCategories/AutoVehicle.svg?react";
import BusinessOffice from "@icons/transactionCategories/BusinessOffice.svg?react";
import CharitableDonationsIcon from "@icons/transactionCategories/CharitableContributions.svg?react";
import CogsIcon from "@icons/transactionCategories/COGS.svg?react";
import CommunicationsIcon from "@icons/transactionCategories/Communications.svg?react";
import ContributionsIcon from "@icons/transactionCategories/Contribution.svg?react";
import ContributionsAndDistributionsIcon from "@icons/transactionCategories/ContributionDistribution.svg?react";
import CreditCardLoansIcons from "@icons/transactionCategories/CreditCardLoans.svg?react";
import DistributionsIcon from "@icons/transactionCategories/Distribution.svg?react";
import DonationsIcon from "@icons/transactionCategories/Donations.svg?react";
import EntertainmentIcon from "@icons/transactionCategories/Entertainment.svg?react";
import FeesIcon from "@icons/transactionCategories/Fees.svg?react";
import HealthDentalIcon from "@icons/transactionCategories/HealthDental.svg?react";
import IncomeIcon from "@icons/transactionCategories/Income.svg?react";
import InsuranceIcon from "@icons/transactionCategories/Insurance.svg?react";
import LearningDevelopmentIcon from "@icons/transactionCategories/LearningDevelopment.svg?react";
import LegalProfessionalIcon from "@icons/transactionCategories/LegalProfessional.svg?react";
import MealsIcon from "@icons/transactionCategories/Meals.svg?react";
import OtherIncomeIcon from "@icons/transactionCategories/OtherIncome.svg?react";
import PayrollIcon from "@icons/transactionCategories/Payroll.svg?react";
import PersonalIcon from "@icons/transactionCategories/Personal.svg?react";
import RentLeasesIcon from "@icons/transactionCategories/RentalsLeases.svg?react";
import RepairsMaintenanceIcon from "@icons/transactionCategories/RepairsMaintenance.svg?react";
import RetirementIcon from "@icons/transactionCategories/Retirement.svg?react";
import SecurityDepositIcon from "@icons/transactionCategories/SecurityDeposit.svg?react";
import SoftwareSubscriptionsIcon from "@icons/transactionCategories/SoftwareSubscriptions.svg?react";
import SuppliesEquipmentIcon from "@icons/transactionCategories/SuppliesEquipment.svg?react";
import TaxesIcon from "@icons/transactionCategories/Taxes.svg?react";
import TransferIcon from "@icons/transactionCategories/Transfer.svg?react";
import TravelIcon from "@icons/transactionCategories/Travel.svg?react";
import UncategorizedIcon from "@icons/transactionCategories/Uncategorized.svg?react";
import UtilitiesIcon from "@icons/transactionCategories/Utilities.svg?react";

interface IconMapFunctionParams {
    transactionDirection: TransactionDirectionType;
}
type IconMapFunction = (params: IconMapFunctionParams) => React.ReactNode;

const ICON_MAP: Partial<Record<Taxonomy, React.ReactNode | IconMapFunction>> = {
    advertising_marketing: <AdvertisingMarketingIcon />,
    auto_vehicle: <AutoVehicleIcon />,
    business_office: <BusinessOffice />,
    communications: <CommunicationsIcon />,
    cogs: <CogsIcon />,
    credit_card_loans: <CreditCardLoansIcons />,
    donations: <DonationsIcon />,
    learning_development: <LearningDevelopmentIcon />,
    entertainment: <EntertainmentIcon />,
    fees: <FeesIcon />,
    health_dental: <HealthDentalIcon />,
    income: <IncomeIcon />,
    other_income: <OtherIncomeIcon />,
    insurance: <InsuranceIcon />,
    legal_professional: <LegalProfessionalIcon />,
    meals: <MealsIcon />,
    payroll: <PayrollIcon />,
    personal: <PersonalIcon />,
    rentals_leases: <RentLeasesIcon />,
    security_deposit: <SecurityDepositIcon />,
    repairs_maintenance: <RepairsMaintenanceIcon />,
    retirement: <RetirementIcon />,
    software_subscriptions: <SoftwareSubscriptionsIcon />,
    supplies_equipment: <SuppliesEquipmentIcon />,
    taxes: <TaxesIcon />,
    transfer: <TransferIcon />,
    contributions_distributions: ({ transactionDirection }) => {
        if (transactionDirection === TransactionDirectionType.incoming) {
            return <ContributionsIcon />;
        }
        if (transactionDirection === TransactionDirectionType.outgoing) {
            return <DistributionsIcon />;
        }
        return <ContributionsAndDistributionsIcon />;
    },
    travel: <TravelIcon />,
    uncategorized: <UncategorizedIcon />,
    utilities: <UtilitiesIcon />,
    assets: <AssetsIcon />,
    charitable_contributions: <CharitableDonationsIcon />,
};

function renderIconByTaxonomy(
    taxonomy: Taxonomy,
    transactionDirection: TransactionDirectionType,
): React.ReactNode {
    const icon = ICON_MAP[taxonomy];
    if (!icon) {
        return null;
    }
    if (typeof icon === "function") {
        return icon({ transactionDirection });
    }
    return icon;
}

interface Props {
    categoryId: string;
    transactionDirection?: TransactionDirectionType;
}

export const CategoryIcon: React.FC<Props> = React.memo(
    ({ categoryId, transactionDirection = TransactionDirectionType.mixed }) => {
        const categoriesById = useCategoriesById();

        const taxonomy = getMainCategory(categoryId, categoriesById)?.taxonomy;

        if (!taxonomy) {
            return null;
        }

        const icon = renderIconByTaxonomy(taxonomy, transactionDirection);
        if (!icon) {
            return null;
        }

        return <span className="icon-color-revert line-height-1">{icon}</span>;
    },
);
CategoryIcon.displayName = "CategoryIcon";

interface CategoryIconFromTaxonomyProps {
    taxonomy: string;
    transactionDirection?: TransactionDirectionType;
}

export const CategoryIconFromTaxonomy: React.FC<CategoryIconFromTaxonomyProps> =
    React.memo(
        ({
            taxonomy,
            transactionDirection = TransactionDirectionType.mixed,
        }) => {
            const icon = renderIconByTaxonomy(
                taxonomy as Taxonomy,
                transactionDirection,
            );
            if (!icon) {
                return null;
            }
            return (
                <span className="icon-color-revert line-height-1">{icon}</span>
            );
        },
    );
CategoryIconFromTaxonomy.displayName = "CategoryIconFromTaxonomy";
